import { ParameterDto, ParameterValueDto } from "dtos/dtos";

/**
 * All parameter keys.
 */
export const PARAMETER_KEY = {
  paramYearlyDecrease: "param_yearly_decrease",
  paramObservPeriod: "param_observ_period",
  paramAreaPerResident: "param_area_per_resident",
  paramHabitationRatio: "param_habitation_ratio",
  paramDevelopRatioCovered: "param_develop_ratio_covered",
  paramDevelopRatioUncovered: "param_develop_ratio_uncovered",
  paramDensificationRatioCovered: "param_densification_ratio_covered",
  paramDensificationRatioUncovered: "param_densification_ratio_uncovered",
  paramUezRatio: "param_uez_ratio",
  paramUez: "param_uez",
  paramBuildingHeight: "param_building_height",
  paramPopulationHist: "param_population_hist",
  paramYearPreviousRp: "param_year_previous_rp",
  paramRelevantYearPopulation: "param_relevant_year_population",
  aParamPopulationActual: "A_param_population_actual",
  paramFactorRp: "param_factor_rp",
  paramPopulationPerApartment3010Best:
    "param_population_per_apartment_3010_best",
  paramPopulationPerApartment3010ImbBew:
    "param_population_per_apartment_3010_imb_bew",
  paramPopulationPerApartment3020Best:
    "param_population_per_apartment_3020_best",
  paramPopulationPerApartment3020ImbBew:
    "param_population_per_apartment_3020_imb_bew",
  paramPopulationPerApartment3030Best:
    "param_population_per_apartment_3030_best",
  paramPopulationPerApartment3030ImbBew:
    "param_population_per_apartment_3030_imb_bew",
  paramPopulationPerApartment3031Best:
    "param_population_per_apartment_3031_best",
  paramPopulationPerApartment3031ImbBew:
    "param_population_per_apartment_3031_imb_bew",
  paramPopulationPerApartment3032Best:
    "param_population_per_apartment_3032_best",
  paramPopulationPerApartment3032ImbBew:
    "param_population_per_apartment_3032_imb_bew",
  paramPopulationPerApartment3033Best:
    "param_population_per_apartment_3033_best",
  paramPopulationPerApartment3033ImbBew:
    "param_population_per_apartment_3033_imb_bew",
  paramPopulationPerApartment3034Best:
    "param_population_per_apartment_3034_best",
  paramPopulationPerApartment3034ImbBew:
    "param_population_per_apartment_3034_imb_bew",
  paramPopulationPerApartment3035Best:
    "param_population_per_apartment_3035_best",
  paramPopulationPerApartment3035ImbBew:
    "param_population_per_apartment_3035_imb_bew",
  paramPopulationPerApartment3036Best:
    "param_population_per_apartment_3036_best",
  paramPopulationPerApartment3036ImbBew:
    "param_population_per_apartment_3036_imb_bew",
  paramPopulationPerApartment3037Best:
    "param_population_per_apartment_3037_best",
  paramPopulationPerApartment3037ImbBew:
    "param_population_per_apartment_3037_imb_bew",
  paramPopulationPerApartment3038Best:
    "param_population_per_apartment_3038_best",
  paramPopulationPerApartment3038ImbBew:
    "param_population_per_apartment_3038_imb_bew",
  paramPopulationPerApartment3070Best:
    "param_population_per_apartment_3070_best",
  paramPopulationPerApartment3070ImbBew:
    "param_population_per_apartment_3070_imb_bew",
  paramPopulationPerApartmentNullBest:
    "param_population_per_apartment_null_best",
  paramPopulationPerApartmentNullImbBew:
    "param_population_per_apartment_null_imb_bew",
  paramBuildingAreaDemand: "param_building_area_demand",
};

/**
 * Represents the level to which a parameter value belongs.
 */
export type ParameterValueLevel =
  | {
      name: "districtZoneGde";
      zonTypGde: number;
      bfsNr1999: number;
    }
  | {
      name: "district";
      bfsNr1999: number;
    }
  | {
      name: "canton";
    };

/**
 * Defines to which parameter and level a parameter value belongs. This information uniquely
 * identifies a parameter value and can be used to hash it.
 */
export class ParameterValueContext {
  parameterKey: string;
  level: ParameterValueLevel;

  constructor(parameterKey: string, level: ParameterValueLevel) {
    this.parameterKey = parameterKey;
    this.level = level;
  }

  getHash(): string {
    let hash = "";

    switch (this.level.name) {
      case "districtZoneGde":
        hash = [
          this.parameterKey,
          this.level.bfsNr1999,
          this.level.zonTypGde,
        ].join("-");
        break;
      case "district":
        hash = [this.parameterKey, this.level.bfsNr1999].join("-");
        break;
      case "canton":
        hash = this.parameterKey;
        break;
    }

    return hash;
  }
}

/**
 * A container for fast retrieval of parameters.
 */
export class Parameters {
  private parameterRecord: Record<string, ParameterDto>;

  constructor(parameters: ParameterDto[]) {
    this.parameterRecord = this.createParameterRecord(parameters);
  }

  getParameter(parameterKey: string): ParameterDto | undefined {
    return this.parameterRecord.hasOwnProperty(parameterKey)
      ? this.parameterRecord[parameterKey]
      : undefined;
  }

  getAllParameters() {
    return Object.values(this.parameterRecord);
  }

  private createParameterRecord(
    parameters: ParameterDto[]
  ): Record<string, ParameterDto> {
    return parameters.reduce<Record<string, ParameterDto>>(
      (result, parameter) => {
        result[parameter.key] = parameter;
        return result;
      },
      {}
    );
  }
}

/**
 * Represents a parameter value. A value is bound to a context.
 */
export class ParameterValue {
  id: number;
  value: number;
  displayValue: string;
  defaultValue: number;
  displayDefaultValue: string;
  context: ParameterValueContext;

  constructor(
    id: number,
    value: number,
    displayValue: string,
    defaultValue: number,
    displayDefaultValue: string,
    context: ParameterValueContext
  ) {
    this.id = id;
    this.value = value;
    this.displayValue = displayValue;
    this.defaultValue = defaultValue;
    this.displayDefaultValue = displayDefaultValue;
    this.context = context;
  }

  getHash(): string {
    return this.context.getHash();
  }
}

/**
 * A container for managing parameter values.
 */
export class ParameterValues {
  private valueRecord: Record<string, ParameterValue>;

  constructor(parameterValues: ParameterValueDto[]) {
    this.valueRecord = this.createParameterValueRecord(parameterValues);
  }

  getValue(context: ParameterValueContext): ParameterValue | undefined {
    const key = context.getHash();

    if (this.valueRecord.hasOwnProperty(key)) {
      return this.valueRecord[key];
    }

    return undefined;
  }

  addValue(parameterValue: ParameterValue): void {
    const key = parameterValue.getHash();
    this.valueRecord[key] = parameterValue;
  }

  createParameterValueDtos(): ParameterValueDto[] {
    return Object.values(this.valueRecord).map((v) => {
      return {
        id: v.id,
        parameterKey: v.context.parameterKey,
        value: v.value,
        displayValue: v.displayValue,
        defaultValue: v.defaultValue,
        displayDefaultValue: v.displayDefaultValue,
        zonTypGde:
          v.context.level.name === "districtZoneGde"
            ? v.context.level.zonTypGde
            : null,
        bfsNr1999:
          v.context.level.name === "districtZoneGde" ||
          v.context.level.name === "district"
            ? v.context.level.bfsNr1999
            : null,
      };
    });
  }

  private createParameterValueRecord(
    parameterValueDtos: ParameterValueDto[]
  ): Record<string, ParameterValue> {
    return parameterValueDtos.reduce<Record<string, ParameterValue>>(
      (parameterValueRecord, parameterValueDto) => {
        let context: ParameterValueContext;

        if (
          parameterValueDto.bfsNr1999 !== null &&
          parameterValueDto.zonTypGde !== null
        ) {
          context = new ParameterValueContext(parameterValueDto.parameterKey, {
            name: "districtZoneGde",
            bfsNr1999: parameterValueDto.bfsNr1999,
            zonTypGde: parameterValueDto.zonTypGde,
          });
        } else if (parameterValueDto.bfsNr1999 !== null) {
          context = new ParameterValueContext(parameterValueDto.parameterKey, {
            name: "district",
            bfsNr1999: parameterValueDto.bfsNr1999,
          });
        } else {
          context = new ParameterValueContext(parameterValueDto.parameterKey, {
            name: "canton",
          });
        }

        const parameterValue = new ParameterValue(
          parameterValueDto.id,
          parameterValueDto.value,
          parameterValueDto.displayValue,
          parameterValueDto.defaultValue,
          parameterValueDto.displayDefaultValue,
          context
        );
        const key = parameterValue.getHash();
        parameterValueRecord[key] = parameterValue;
        return parameterValueRecord;
      },
      {}
    );
  }
}
